import React, { useState, useRef, useCallback } from 'react';
import { ProjectFile } from '../../types';
import {
  createFile,
  deleteFile,
  deleteFileStorageContent,
  formatBytes,
  isStorageBackedFile,
  LARGE_FILE_THRESHOLD_BYTES,
  updateFile,
  uploadFileContentToStorage,
  uploadProjectImage,
} from '../../services/projects';
import {
  Database,
  Download,
  FileText,
  FileCode,
  Folder,
  FolderOpen,
  MoreHorizontal,
  MoreVertical,
  Trash2,
  Pencil,
  Upload,
  Image,
} from 'lucide-react';
import { ConfirmModal } from '../ConfirmModal';
import { downloadProjectZip } from '../../utils/projectZip';

interface FileTreeProps {
  files: ProjectFile[];
  activeFileId?: string;
  onFileSelect: (file: ProjectFile) => void;
  projectId: string;
  projectTitle?: string;
  onDownloadZip?: (projectTitle: string, files: ProjectFile[]) => Promise<void>;
  onFilesChange: (files: ProjectFile[]) => void;
  canEdit?: boolean;
}

/** Characters that are special in LaTeX and may cause compilation issues. */
const UNSAFE_FILENAME_CHARS = /[_#%&{}~^]/;

function getFileType(name: string): ProjectFile['type'] {
  if (/\.(png|jpe?g)$/i.test(name)) return 'image';
  if (name.endsWith('.tex') || name.endsWith('.sty') || name.endsWith('.cls')) return 'tex';
  if (name.endsWith('.bib')) return 'bib';
  return 'other';
}

function getSafeName(name: string): string {
  return name.replace(/^(.+?)(\.[^.]+)?$/, (_, base, ext) =>
    base.replace(/[_#%&{}~^]/g, '-') + (ext ?? '')
  );
}

function hasUnsafeChars(name: string): boolean {
  return UNSAFE_FILENAME_CHARS.test(name.replace(/\.[^.]+$/, ''));
}

interface ModalState {
  type: 'delete' | 'unsafe-name';
  /** For delete: the file ID. For unsafe-name: the action to perform on confirm. */
  fileId?: string;
  fileName?: string;
  safeName?: string;
  onProceed: () => void;
}

export function FileTree({
  files,
  activeFileId,
  onFileSelect,
  projectId,
  projectTitle = 'project',
  onDownloadZip,
  onFilesChange,
  canEdit = true,
}: FileTreeProps) {
  const [showNewFileInput, setShowNewFileInput] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [modal, setModal] = useState<ModalState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingActionRef = useRef(false);

  const dismissModal = useCallback(() => setModal(null), []);

  const confirmModal = useCallback(() => {
    if (modal) {
      modal.onProceed();
      setModal(null);
    }
  }, [modal]);

  function getFileIcon(file: ProjectFile) {
    if (file.type === 'folder') {
      return expandedFolders.has(file.id) ? (
        <FolderOpen className="h-4 w-4 text-yellow-500" />
      ) : (
        <Folder className="h-4 w-4 text-yellow-500" />
      );
    }
    if (file.type === 'image') {
      return <Image className="h-4 w-4 text-green-500" />;
    }
    if (isStorageBackedFile(file)) {
      return <Database className="h-4 w-4 text-amber-500" />;
    }
    if (file.name.endsWith('.bib')) {
      return <FileCode className="h-4 w-4 text-purple-500" />;
    }
    if (file.type === 'other') {
      return <FileText className="h-4 w-4 text-gray-500" />;
    }
    return <FileText className="h-4 w-4 text-blue-500" />;
  }

  async function doCreateFile(name: string) {
    const type = getFileType(name);
    const defaultContent =
      type === 'tex'
        ? '% New LaTeX file\n'
        : type === 'bib'
        ? '% Bibliography file\n% Add entries like:\n%\n% @article{key,\n%   author  = {Author Name},\n%   title   = {Article Title},\n%   journal = {Journal Name},\n%   year    = {2024},\n% }\n\n'
        : type === 'other'
        ? ''
        : '';

    try {
      const fileId = await createFile(projectId, {
        name,
        content: defaultContent,
        type,
        parentId: null,
      });
      const newFile: ProjectFile = {
        id: fileId,
        name,
        content: defaultContent,
        type,
        parentId: null,
      };
      onFilesChange([...files, newFile]);
      setShowNewFileInput(false);
      setNewFileName('');
      onFileSelect(newFile);
    } catch (error) {
      console.error('Failed to create file:', error);
    } finally {
      pendingActionRef.current = false;
    }
  }

  function handleCreateFile() {
    if (!newFileName.trim() || pendingActionRef.current) return;
    pendingActionRef.current = true;
    const name = newFileName.trim();

    if (hasUnsafeChars(name)) {
      setModal({
        type: 'unsafe-name',
        fileName: name,
        safeName: getSafeName(name),
        onProceed: () => doCreateFile(name),
      });
      return;
    }

    doCreateFile(name);
  }

  async function doDeleteFile(fileId: string) {
    try {
      const fileToDelete = files.find((f) => f.id === fileId);
      if (fileToDelete?.storageContentPath) {
        await deleteFileStorageContent(fileToDelete.storageContentPath).catch(() => {
          // Storage object may already be gone — continue with Firestore delete
        });
      }
      await deleteFile(projectId, fileId);
      onFilesChange(files.filter((f) => f.id !== fileId));
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
    setActiveMenu(null);
  }

  function handleDeleteFile(fileId: string) {
    const fileToDelete = files.find((f) => f.id === fileId);
    setModal({
      type: 'delete',
      fileId,
      fileName: fileToDelete?.name,
      onProceed: () => doDeleteFile(fileId),
    });
  }

  async function doRenameFile(fileId: string, name: string) {
    try {
      await updateFile(projectId, fileId, { name });
      onFilesChange(files.map((f) => (f.id === fileId ? { ...f, name } : f)));
      setEditingId(null);
      setEditingName('');
    } catch (error) {
      console.error('Failed to rename file:', error);
    } finally {
      pendingActionRef.current = false;
    }
  }

  function handleRenameFile(fileId: string) {
    if (!editingName.trim() || pendingActionRef.current) return;
    pendingActionRef.current = true;
    const name = editingName.trim();

    if (hasUnsafeChars(name)) {
      setModal({
        type: 'unsafe-name',
        fileName: name,
        safeName: getSafeName(name),
        onProceed: () => doRenameFile(fileId, name),
      });
      return;
    }

    doRenameFile(fileId, name);
  }

  function startEditing(file: ProjectFile) {
    setEditingId(file.id);
    setEditingName(file.name);
    setActiveMenu(null);
  }

  function toggleFolder(folderId: string) {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }

  async function handleUploadFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    const name = file.name;
    const type = getFileType(name);
    setUploading(true);

    try {
      if (type === 'image') {
        const content = await uploadProjectImage(projectId, name, file);
        const fileId = await createFile(projectId, { name, content, type, parentId: null });
        const newFile: ProjectFile = { id: fileId, name, content, type, parentId: null };
        onFilesChange([...files, newFile]);
        onFileSelect(newFile);
        return;
      }

      const buffer = await file.arrayBuffer();

      if (buffer.byteLength > LARGE_FILE_THRESHOLD_BYTES) {
        // Large file: store content in Cloud Storage, Firestore gets metadata only
        const fileId = await createFile(projectId, {
          name,
          content: '',
          type,
          parentId: null,
          otEnabled: false,
        });
        const storagePath = await uploadFileContentToStorage(projectId, fileId, buffer);
        await updateFile(projectId, fileId, {
          storageContentPath: storagePath,
          storageSizeBytes: buffer.byteLength,
        });
        const newFile: ProjectFile = {
          id: fileId,
          name,
          content: '',
          type,
          parentId: null,
          otEnabled: false,
          storageContentPath: storagePath,
          storageSizeBytes: buffer.byteLength,
        };
        onFilesChange([...files, newFile]);
        onFileSelect(newFile);
      } else {
        // Small file: inline content in Firestore (current behavior)
        const content = new TextDecoder().decode(buffer);
        const fileId = await createFile(projectId, { name, content, type, parentId: null });
        const newFile: ProjectFile = { id: fileId, name, content, type, parentId: null };
        onFilesChange([...files, newFile]);
        onFileSelect(newFile);
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDownloadZip() {
    if (files.length === 0 || downloadingZip) return;

    setShowHeaderMenu(false);
    setDownloadingZip(true);

    try {
      if (onDownloadZip) {
        await onDownloadZip(projectTitle, files);
      } else {
        await downloadProjectZip(projectTitle, files);
      }
    } catch (error) {
      console.error('Failed to download project zip:', error);
    } finally {
      setDownloadingZip(false);
    }
  }

  // Build tree structure
  const rootFiles = files.filter((f) => f.parentId === null);

  return (
    <div className="p-2">
      {/* Header with add button */}
      <div className="flex items-center justify-between px-2 py-1 mb-1">
        <span className="text-xs font-medium text-gray-500 uppercase">Files</span>
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setActiveMenu(null);
              setShowHeaderMenu(!showHeaderMenu);
            }}
            disabled={uploading || downloadingZip}
            aria-label="File actions"
            className="p-1 rounded hover:bg-gray-200"
            title="File actions"
          >
            {uploading || downloadingZip ? (
              <div className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <MoreHorizontal className="h-4 w-4 text-gray-600" />
            )}
          </button>
          {showHeaderMenu && (
            <div className="absolute right-0 top-full mt-1 w-40 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-lg z-20">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => {
                    setShowHeaderMenu(false);
                    setShowNewFileInput(true);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <FileText className="h-3.5 w-3.5" />
                  New File
                </button>
              )}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => {
                    setShowHeaderMenu(false);
                    fileInputRef.current?.click();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Upload className="h-3.5 w-3.5" />
                  Upload File
                </button>
              )}
              {canEdit && (
                <div className="mx-2 border-t border-gray-100" />
              )}
              <button
                type="button"
                onClick={() => { void handleDownloadZip(); }}
                disabled={files.length === 0 || downloadingZip}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400 disabled:hover:bg-white"
              >
                <Download className="h-3.5 w-3.5" />
                {downloadingZip ? 'Downloading...' : 'Download Zip'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept=""
        onChange={handleUploadFile}
        className="hidden"
      />

      {/* New file input */}
      {showNewFileInput && (
        <div className="px-2 py-1 mb-1">
          <input
            type="text"
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFile();
              if (e.key === 'Escape') {
                setShowNewFileInput(false);
                setNewFileName('');
              }
            }}
            onBlur={() => {
              if (newFileName.trim()) handleCreateFile();
              else {
                setShowNewFileInput(false);
                setNewFileName('');
              }
            }}
            placeholder="filename.tex"
            autoFocus
            className="w-full px-2 py-1 text-sm border border-blue-500 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
      )}

      {/* File list */}
      <div className="space-y-0.5">
        {rootFiles.map((file) => (
          <div key={file.id} className="relative group">
            {editingId === file.id ? (
              <input
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameFile(file.id);
                  if (e.key === 'Escape') {
                    setEditingId(null);
                    setEditingName('');
                  }
                }}
                onBlur={() => handleRenameFile(file.id)}
                autoFocus
                className="w-full px-2 py-1 text-sm border border-blue-500 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            ) : (
              <div
                onClick={() =>
                  file.type === 'folder'
                    ? toggleFolder(file.id)
                    : onFileSelect(file)
                }
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer ${
                  activeFileId === file.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                {getFileIcon(file)}
                <span className="flex-1 text-sm truncate">{file.name}</span>
                {isStorageBackedFile(file) && file.storageSizeBytes != null && (
                  <span className="shrink-0 text-[10px] text-gray-400">{formatBytes(file.storageSizeBytes)}</span>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowHeaderMenu(false);
                    setActiveMenu(activeMenu === file.id ? null : file.id);
                  }}
                  disabled={!canEdit}
                  aria-label={`Actions for ${file.name}`}
                  className="p-0.5 rounded hover:bg-gray-200 opacity-0 group-hover:opacity-100"
                >
                  <MoreVertical className="h-3.5 w-3.5 text-gray-500" />
                </button>
              </div>
            )}

            {/* Context menu */}
            {canEdit && activeMenu === file.id && (
              <div className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                <button
                  type="button"
                  onClick={() => startEditing(file)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Rename
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteFile(file.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty state */}
      {files.length === 0 && !showNewFileInput && (
        <div className="text-center py-8">
          <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No files yet</p>
        </div>
      )}

      {/* Close menus on outside click */}
      {(activeMenu || showHeaderMenu) && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            setActiveMenu(null);
            setShowHeaderMenu(false);
          }}
        />
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        open={modal?.type === 'delete'}
        title="Delete file"
        message={`Are you sure you want to delete "${modal?.fileName ?? 'this file'}"? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={confirmModal}
        onCancel={() => {
          dismissModal();
          pendingActionRef.current = false;
        }}
      />

      {/* Unsafe filename warning modal */}
      <ConfirmModal
        open={modal?.type === 'unsafe-name'}
        title="Filename may cause issues"
        message={`"${modal?.fileName ?? ''}" contains characters (like underscores) that can cause LaTeX compilation errors.`}
        detail={`Suggested alternative: "${modal?.safeName ?? ''}"`}
        confirmLabel="Use anyway"
        cancelLabel="Go back"
        variant="warning"
        onConfirm={confirmModal}
        onCancel={() => {
          dismissModal();
          pendingActionRef.current = false;
        }}
      />
    </div>
  );
}
